import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import DirectionsIcon from '@mui/icons-material/Directions';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Section,
  SectionHeading,
  PageHero,
  PrimaryButton,
  IconText,
  InfoTile,
} from '../components/ui';
import usePageMeta from '../hooks/usePageMeta';
import './styles/Join.css';

// Global constants (CAPITALIZED_SNAKE_CASE): stable external values reused
// across the page. Kept module-private (not exported) per scoping rules.
const SERVICE_TIME = 'Sundays at 10:45 AM';
const ADDRESS = '4301 Dalrock Road, Rowlett, TX 75088';
const DIRECTIONS_URL = 'https://maps.app.goo.gl/aptJc46XDx7rMGwG7';
const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d837.4761177952921!2d-96.5196937!3d32.9006939!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864ea9dc8024ae1f%3A0xd7bbc9e917ccf7a!2sNew%20Horizon%20Vineyard!5e0!3m2!1sen!2sus!4v1748026039587!5m2!1sen!2sus';

// "What to expect" bullets: local, presentational data.
const EXPECT_ITEMS = [
  {
    icon: <FavoriteBorderIcon />,
    text: 'A warm welcome the moment you walk in. Come as you are, no pressure.',
  },
  {
    icon: <GroupsIcon />,
    text: 'Heartfelt worship and a practical, encouraging message from the Bible.',
  },
  {
    icon: <FamilyRestroomIcon />,
    text: 'Children worship with us, then enjoy age-appropriate Children’s Ministry.',
  },
  {
    icon: <CheckroomIcon />,
    text: 'No dress code. Wear whatever feels comfortable to you.',
  },
];

// FAQ content: local, presentational data.
const FAQ_ITEMS = [
  {
    id: 'panel1',
    question: 'What time does service start?',
    answer: 'Service begins at 10:45 AM on Sunday morning.',
  },
  {
    id: 'panel2',
    question: 'What should I wear?',
    answer:
      'At New Horizon Vineyard, it’s all about real people and real connection. Just come as you are, no dress code here.',
  },
  {
    id: 'panel3',
    question: 'Is there childcare?',
    answer:
      'We have something for the entire family on Sunday morning! Everyone worships together in the main sanctuary, and after worship the children are welcome to participate in the Children’s Ministry, where there will be age-appropriate Bible stories, activities, and discussion.',
  },
  {
    id: 'panel4',
    question: 'How do I connect online?',
    answer: 'youtube',
  },
];

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  // neutral.200 (#DDE3EA): cool-gray hairline from the Joy palette. Material's
  // `styled` engine cannot read Joy tokens off `theme`, so the CSS var is used.
  border: '1px solid var(--joy-palette-neutral-200)',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  // primary.50 (#EAF0F7): subtle navy tint replacing the old rgba(0,100,148,.05).
  backgroundColor: 'var(--joy-palette-primary-50)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(90deg)',
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid var(--joy-palette-neutral-200)',
}));

const Join = () => {
  usePageMeta(
    'Plan Your Visit',
    'Everything you need for your first Sunday at New Horizon Vineyard in Rowlett, TX: ' +
      'service time, directions, what to expect, and children’s ministry.',
  );
  const [expanded, setExpanded] = React.useState('');

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Box component="main">
      <PageHero
        title="Plan Your Visit"
        eyebrow="We’d Love To Meet You"
        subtitle="Whether it’s your first time at church or you’re looking for a new home, we’ve saved you a seat. Here’s everything you need to know before Sunday."
        height="sm"
        overlay="strong"
      >
        <PrimaryButton href={DIRECTIONS_URL} color="accent" startDecorator={<DirectionsIcon />}>
          Get Directions
        </PrimaryButton>
        <PrimaryButton to="/contact" inverted>
          Ask a Question
        </PrimaryButton>
      </PageHero>

      {/* At-a-glance: when & where */}
      <Section bg="default" padding="md">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          sx={{ justifyContent: 'space-around' }}
        >
          <InfoTile
            icon={<AccessTimeIcon />}
            label="Service Time"
            value={SERVICE_TIME}
          />
          <InfoTile
            icon={<PlaceIcon />}
            label="Location"
            value={ADDRESS}
            href={DIRECTIONS_URL}
          />
        </Stack>
      </Section>

      {/* Welcome message (preserved real content) */}
      <Section bg="tint" padding="md" maxWidth="md">
        <SectionHeading
          eyebrow="Sunday Morning Experience"
          title="Come as you are"
          align="center"
        />
        <Typography level="body-lg" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          Our hope is that you will find NHV a comfortable and friendly place. We want you to
          feel at ease and be free to learn more about what it means to have a relationship
          with God. We believe this is the key to everything, including all other
          relationships. That is why our vision begins with helping people draw closer in their
          relationship with God. This will influence how they grow stronger in their
          relationship with each other and how they live louder in their relationship with the
          world. So we encourage you to come as you are and hang out with us some Sunday
          morning.
        </Typography>
      </Section>

      {/* What to expect */}
      <Section bg="default" padding="md">
        <SectionHeading
          eyebrow="What To Expect"
          title="Your first Sunday with us"
          subtitle="Here’s a glimpse of what a visit looks like, so nothing feels unfamiliar when you arrive."
          align="center"
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: 2.5, md: 4 },
            mt: 2,
            maxWidth: 760,
            mx: 'auto',
          }}
        >
          {EXPECT_ITEMS.map((item, index) => (
            <IconText key={index} icon={item.icon} align="start" level="body-lg">
              {item.text}
            </IconText>
          ))}
        </Box>
      </Section>

      {/* Where we are + map */}
      <Section bg="tint" padding="md">
        <SectionHeading eyebrow="Find Us" title="Where we are located" align="center" />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          sx={{ alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            component="iframe"
            src={MAP_EMBED_SRC}
            title="NHV-Map"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sx={{
              border: 0,
              borderRadius: 'md',
              boxShadow: 'sm',
              width: { xs: '100%', md: 560 },
              height: { xs: 280, md: 400 },
            }}
          />
          <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Typography level="title-lg" sx={{ color: 'text.primary' }}>
              {ADDRESS}
            </Typography>
            <PrimaryButton
              href={DIRECTIONS_URL}
              startDecorator={<DirectionsIcon />}
            >
              Get Directions
            </PrimaryButton>
          </Stack>
        </Stack>
      </Section>

      {/* FAQ */}
      <Section bg="default" padding="md" maxWidth="md">
        <SectionHeading eyebrow="Good To Know" title="Frequently asked questions" align="center" />
        <Box>
          {FAQ_ITEMS.map((item) => (
            <Accordion
              key={item.id}
              expanded={expanded === item.id}
              onChange={handleChange(item.id)}
            >
              <AccordionSummary
                aria-controls={`${item.id}d-content`}
                id={`${item.id}d-header`}
              >
                <Typography level="title-lg" component="span">
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {item.answer === 'youtube' ? (
                  <Typography level="body-lg">
                    For the Sunday service you can connect via our{' '}
                    <a
                      className="faqLink"
                      href="https://www.youtube.com/@newhorizonvineyardchurch4635"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      YouTube channel
                    </a>
                  </Typography>
                ) : (
                  <Typography level="body-lg">{item.answer}</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Section>

      {/* Closing CTA */}
      <Section bg="brand" padding="md" maxWidth="md">
        <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <SectionHeading
            title="Still have questions?"
            subtitle="We’re happy to help you plan your visit. Reach out and someone from our team will get back to you."
            align="center"
            inverted
          />
          <PrimaryButton to="/contact" color="accent">
            Contact Us
          </PrimaryButton>
        </Stack>
      </Section>
    </Box>
  );
};

export default Join;
